import { LogManager } from '@src/utility/Logger';
import axios from 'axios';
import { z } from 'zod';

// Result discriminated union per service layer conventions
export type ServiceErrorKind =
  | 'network'
  | 'unauthorized'
  | 'invalid_payload'
  | 'unknown';

export interface ServiceError {
  kind: ServiceErrorKind;
  message: string;
  cause?: unknown;
  status?: number;
}

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: ServiceError };

// ---------------------------------------------------------------------------
// Schema Definitions (Zod)
// ---------------------------------------------------------------------------
// We allow passthrough to remain forward-compatible if backend adds fields.
const UploadTempCredentialsSchema = z
  .object({
    accessKeyId: z.string().min(1),
    secretAccessKey: z.string().min(1),
    sessionToken: z.string().min(1),
    // Use datetime() for ISO8601 validation; if backend ever relaxes we can broaden.
    expiration: z.string().datetime({ message: 'Invalid ISO timestamp' }),
  })
  .passthrough();

const UploadCredentialsResponseSchema = z
  .object({
    bucket: z.string().min(1),
    region: z.string().min(1).default('us-east-2'),
    keyPrefix: z.string().min(1), // e.g. externalUploads/{orgId}/
    credentials: UploadTempCredentialsSchema,
  })
  .passthrough();

export type UploadTempCredentialsPayload = z.infer<
  typeof UploadTempCredentialsSchema
>;
export type UploadCredentialsResponse = z.infer<
  typeof UploadCredentialsResponseSchema
>;

// Manual type guard helpers replaced with Zod schemas above.

function isAxiosError(err: unknown): err is { response?: { status?: number } } {
  if (typeof err !== 'object' || err === null) return false;
  if (!('response' in err)) return false;
  const rec: Record<string, unknown> = Object.assign({}, err);
  const resp = rec.response;
  return typeof resp === 'object' && resp !== null;
}

const logger = LogManager.getLogger('service:UploadCredentialsService');

export class UploadCredentialsService {
  private endpoint: string;

  constructor(apiOrigin = process.env.REACT_APP_API_URL) {
    this.endpoint = `${apiOrigin}/api/uploads/credentials`;
  }

  async fetch(
    orgId: string,
    authToken?: string,
  ): Promise<Result<UploadCredentialsResponse>> {
    try {
      // Ensure auth token is fresh before making request
      const resp = await axios.post(
        this.endpoint,
        { orgId },
        {
          withCredentials: true,
          headers: authToken
            ? {
                Authorization: `Bearer ${authToken}`,
              }
            : undefined,
        },
      );
      if (resp.status === 401 || resp.status === 403) {
        return {
          ok: false,
          error: {
            kind: 'unauthorized',
            message: 'Not authorized to request upload credentials',
            status: resp.status,
          },
        };
      }
      const parsed = UploadCredentialsResponseSchema.safeParse(resp.data);
      if (!parsed.success) {
        logger.warn('invalid-upload-credentials-payload', {
          issues: parsed.error.issues.slice(0, 5),
          rawSample: Object.keys(resp.data || {}).slice(0, 5),
        });
        return {
          ok: false,
          error: {
            kind: 'invalid_payload',
            message: 'Upload credentials payload failed validation',
            status: resp.status,
          },
        };
      }
      return { ok: true, value: parsed.data };
    } catch (rawErr: unknown) {
      const status = isAxiosError(rawErr) ? rawErr.response?.status : undefined;
      if (status === 401 || status === 403) {
        return {
          ok: false,
          error: {
            kind: 'unauthorized',
            message: 'Unauthorized fetching upload credentials',
            status,
            cause: rawErr,
          },
        };
      }
      if (status) {
        return {
          ok: false,
          error: {
            kind: 'network',
            message: `Network error status ${status}`,
            status,
            cause: rawErr,
          },
        };
      }
      logger.error('upload-credentials-unknown-error', { err: rawErr });
      return {
        ok: false,
        error: { kind: 'unknown', message: 'Unknown error', cause: rawErr },
      };
    }
  }
}

export const uploadCredentialsService = new UploadCredentialsService();
